// @ts-strict-ignore
import React, { memo, useCallback } from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { IconButton, Tooltip } from '@mui/material'
import { getCurrentLocationFull } from 'services/router'
import AttachmentIcon from '@mui/icons-material/Attachment'
import { FaFacebook, FaTwitter, FaLinkedin } from 'react-icons/fa'
import { classList } from 'services/styles'
import { isGuest } from 'services/user'
import { useSnackbar } from 'hooks/redux'
import { useRouter } from 'next/router'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { ShareWidget_GetCurrentUserQuery } from '__generated__/ShareWidget_GetCurrentUserQuery.graphql'
import { makeStyles } from 'tss-react/mui'

interface IShareWidgetProps {
  url?: string
  disabled?: boolean
  referral?: boolean
  className?: string
}

const useStyles = makeStyles()(() => ({
  shareButtons: {
    display: 'flex',
    '& > *': {
      verticalAlign: 'middle',
    },
  },
}))

const ShareWidget = (props: IShareWidgetProps) => {
  const { disabled, url: urlProp, className, referral, ...restProps } = props

  const { classes } = useStyles()
  const router = useRouter()
  const url = urlProp || getCurrentLocationFull(router)

  const user = useLazyLoadQuery<ShareWidget_GetCurrentUserQuery>(graphql`
    query ShareWidget_GetCurrentUserQuery {
      currentUser {
        ...user_isGuest
      }
    }
  `, {}, {
    fetchPolicy: 'store-or-network',
  })

  const { showSnackbarMessage } = useSnackbar()
  const guest = isGuest(user?.currentUser)
  const hasReferral = Boolean(referral)

  const handleCopyLinkClick = useCallback(async () => {
    if (disabled) return

    if (hasReferral) {
      if (guest) {
        showSnackbarMessage('Link copied to clipboard. To share a referral link instead, log in to your account.', { level: 'info' })
      } else {
        showSnackbarMessage('Referral link copied to clipboard', { level: 'info' })
      }
    } else {
      showSnackbarMessage('Link copied to clipboard', { level: 'info' })
    }
  }, [disabled, guest, hasReferral, showSnackbarMessage])

  const handleFBShareClick = useCallback(() => {
    window.open(
      `https://www.facebook.com/sharer.php?u=${encodeURIComponent(url)}`,
      '_blank',
      'location=yes,height=570,width=520,scrollbars=yes,status=yes',
    )
  }, [url])

  const handleTwitterShareClick = useCallback(() => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(url)}`,
      '_blank',
      'location=yes,height=570,width=520,scrollbars=yes,status=yes',
    )
  }, [url])

  const handleLinkedinShareClick = useCallback(() => {
    window.open(
      `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}`,
      '_blank',
    )
  }, [url])

  return (
    <div className={classList(classes.shareButtons, className)} {...restProps}>

      <Tooltip title="Copy link">
        <span style={{ display: 'inline-block' }}>
          <CopyToClipboard
            text={url}
            onCopy={handleCopyLinkClick}
          >
            <IconButton
              onClick={e => e.preventDefault()}
              disabled={disabled}
              size="large"
            >
              <AttachmentIcon />
            </IconButton>
          </CopyToClipboard>
        </span>
      </Tooltip>

      <Tooltip title="Share on Facebook">
        <IconButton onClick={handleFBShareClick} style={{ fontSize: '1.5rem' }} disabled={disabled} size="large">
          <FaFacebook />
        </IconButton>
      </Tooltip>

      <Tooltip title="Share on LinkedIn">
        <IconButton onClick={handleLinkedinShareClick} style={{ fontSize: '1.5rem' }} disabled={disabled} size="large">
          <FaLinkedin />
        </IconButton>
      </Tooltip>

      <Tooltip title="Share on Twitter">
        <IconButton onClick={handleTwitterShareClick} style={{ fontSize: '1.5rem' }} disabled={disabled} size="large">
          <FaTwitter />
        </IconButton>
      </Tooltip>
    </div>
  )
}

export default memo(ShareWidget)
